import React, { createContext, useMemo, useRef, useState } from 'react';

type Ctx = {
  dirty: boolean;
  dirtyRef: React.MutableRefObject<boolean>;
  setDirty: (v: boolean) => void;
  markClean: () => void;
  onDiscard: (() => void) | null;
  setOnDiscard: (v: (() => void) | null) => void;
};
export const UnsavedChangesContext = createContext<Ctx | null>(null);

/**
 * Holds the dirty flag. The guard renders from `AuthenticatedAppWrapper`, which picks the
 * legacy or Aspen variant.
 */
export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dirtyRef = useRef(false);
  const [dirty, setDirtyState] = useState(false);
  const [onDiscard, setOnDiscard] = useState<(() => void) | null>(null);

  const setDirty = React.useCallback((value: boolean) => {
    // Write the ref first. A navigate in this turn reads it before `dirty` renders.
    dirtyRef.current = value;
    setDirtyState(value);
  }, []);

  const markClean = React.useCallback(() => setDirty(false), [setDirty]);
  const value = useMemo(
    () => ({ dirty, dirtyRef, setDirty, markClean, onDiscard, setOnDiscard }),
    [dirty, markClean, onDiscard, setDirty, setOnDiscard],
  );

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
};
